// Date / time helpers. Convention: every date crossing a function or DTO
// boundary in this codebase is an ISO 8601 string (e.g. `'2026-05-04T12:34:56.000Z'`).
// `Date` instances live only inside this file.
//
// Mirrors the surface of `investor-service`'s datetime module so frontend and
// backend speak the same vocabulary. The implementation is native (no dayjs)
// — if we ever need timezone-aware parsing, calendar-quarter math, or
// `endOf('day')` semantics, add dayjs in that PR; call sites stay the same.

// ── Durations ────────────────────────────────────────────────────────────
// Canonical millisecond constants. Use these for staleTime, gcTime, debounce
// intervals, setTimeout values. Importing here keeps every cache config and
// timeout in the codebase speaking the same vocabulary.
//
// The literals below are the *definitions* of those durations — this is the
// one block in the codebase where they're allowed.

export const ONE_SECOND_MS = 1000
export const THIRTY_SECONDS_MS = ONE_SECOND_MS * 30
export const ONE_MINUTE_MS = ONE_SECOND_MS * 60
export const TWO_MINUTES_MS = ONE_MINUTE_MS * 2
export const FIVE_MINUTES_MS = ONE_MINUTE_MS * 5
export const TEN_MINUTES_MS = ONE_MINUTE_MS * 10
export const ONE_HOUR_MS = ONE_MINUTE_MS * 60
export const ONE_DAY_MS = ONE_HOUR_MS * 24

/** Backend-symmetric alias for {@link ONE_DAY_MS}. */
export const MS_PER_DAY = ONE_DAY_MS

// ── Boundary parsing ─────────────────────────────────────────────────────
// Throws on invalid ISO strings so a malformed value fails loud at the
// boundary instead of producing NaN downstream.
const parseIso = (value: string): Date => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid ISO date string: ${value}`)
  }
  return date
}

// ── Now helpers ──────────────────────────────────────────────────────────

/** Current instant as an ISO 8601 string. */
export const nowIso = (): string => new Date().toISOString()

// ── Domain math ──────────────────────────────────────────────────────────

export const isFuture = (iso: string | null | undefined): boolean =>
  !!iso && parseIso(iso).getTime() > Date.now()

export const isPast = (iso: string | null | undefined): boolean =>
  !!iso && parseIso(iso).getTime() < Date.now()

/**
 * Days from now until `iso`. Past dates clamp to 0.
 * Returns null when `iso` is missing.
 */
export const daysFromNowUntil = (iso: string | null | undefined): number | null => {
  if (!iso) return null
  return Math.max(0, Math.ceil((parseIso(iso).getTime() - Date.now()) / ONE_DAY_MS))
}

/**
 * Days from now until `iso`, only when strictly in the future.
 * Returns null when `iso` is missing OR past/now.
 */
export const daysUntilFuture = (iso: string | null | undefined): number | null => {
  if (!iso) return null
  const delta = parseIso(iso).getTime() - Date.now()
  return delta > 0 ? Math.ceil(delta / ONE_DAY_MS) : null
}

/** ms from now until `iso` (positive when `iso` is in the future). */
export const msFromNow = (iso: string): number => parseIso(iso).getTime() - Date.now()

/** ms elapsed since `iso` (positive when `iso` is in the past). */
export const msSince = (iso: string): number => Date.now() - parseIso(iso).getTime()

// ── Formatters ───────────────────────────────────────────────────────────
//
// Canonical set — pick the one whose name describes the output you want:
//
//   formatDate(iso)      →  "18 Mar 2026"                  Date only. THE canonical date format for tables,
//                                                          lists, detail rows — anywhere you only need the date.
//   formatDateTime(iso)  →  "18 Mar 2026 · 09:26 AM"       Date + time. ONLY where time is operationally meaningful
//                                                          (audit log, status change rows, "Last edited at...", txn timestamps).
//   formatTimeAgo(iso)   →  "5 min ago" / "yesterday" / …  Relative time. Recent activity, last-seen displays.
//
// Date-only is the default. Reach for `formatDateTime` only when the surface
// genuinely needs the time-of-day to be visible — operators correlating with
// Slack/email timestamps, audit trails, KYC review log rows.

/** "18 Mar 2026" — the canonical date format. */
export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/** "18 Mar 2026 · 09:26 AM" — use only when the time of day is meaningful. */
export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const date = new Date(iso)
  const dateLabel = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  const timeLabel = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
  return `${dateLabel} · ${timeLabel}`
}

/** "5 min ago" / "yesterday" / "3d ago" — relative time for recent activity. */
export const formatTimeAgo = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(ms / ONE_MINUTE_MS)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  if (hours < 24) return `${hours}h ago`
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}

/**
 * Duration elapsed since `iso` expressed as a human-readable wait label.
 * "45 min" / "2h 30m" / "3 days" — no "ago" suffix.
 * Intended for queue "Waiting since" displays where the operator needs
 * to judge urgency at a glance.
 */
export const formatWaitDuration = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(ms / ONE_MINUTE_MS)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m`
  if (hours < 24) {
    const remainingMin = minutes % 60
    return remainingMin > 0 ? `${hours}h ${remainingMin}m` : `${hours}h`
  }
  const remainingHours = hours % 24
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

/** "10:34 AM" — time-only display for "waiting since" / "last seen" labels. */
export const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

/** "18 Mar 2026" — alias for {@link formatDate}. */
export const formatDisplayDate = formatDate

/** "18 Mar 2026 · 09:26 AM" — alias for {@link formatDateTime}. */
export const formatTimestamp = formatDateTime

/**
 * Convert a `Date` (or null) to an ISO 8601 string, returning `undefined` for
 * null/missing inputs. Useful for translating date-picker `Date` values into
 * query filters that expect ISO strings.
 */
export const dateToIso = (date: Date | null): string | undefined => {
  if (!date) return undefined
  return new Date(date).toISOString()
}

/**
 * Convert a `Date` (or null) to a date-only `YYYY-MM-DD` string in local time,
 * returning null for null/missing inputs. Use for API fields that expect a
 * calendar date (e.g. date of birth) rather than a full ISO timestamp.
 */
export const dateToYmd = (date: Date | null): string | null => {
  if (!date) return null
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Whole-years age for a date of birth (`Date` or ISO string). Returns null for
 * missing / unparseable input. Accounts for whether this year's birthday has
 * already passed.
 */
export const getAge = (date: Date | string | null | undefined): number | null => {
  if (!date) return null
  const dob = typeof date === 'string' ? parseIso(date) : date
  if (Number.isNaN(dob.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const monthDelta = today.getMonth() - dob.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) age--
  return age
}
